import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";

// Material
import { makeStyles } from "@material-ui/core/styles";

// Actions
import { parseFetchOptions } from "src/state/user/actions";

// Config
import { getStatementsUrl } from "src/config/apiConfig";
import { statementRequestSources, statementStatus } from "src/config/values";
import { getRoute, urls } from "src/config/routes";

// Hooks
import { useAppInsights } from "src/utils/telemetry/AppInsights";

// Context
import RecipientsProvider from "src/context/RecipientsProvider";

// Components
import PendingStatementItem from "../PendingStatementItem";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: theme.spacing(2)
  }
}));

const PENDING_STATES = [statementStatus.PENDING, statementStatus.DRAFT];

const sortByMostRecent = items =>
  [...items].sort((a, b) => {
    const i = a.updateDateTime || a.creationDateTime;
    const j = b.updateDateTime || b.creationDateTime;
    if (i > j) return -1;
    if (i < j) return 1;
    return 0;
  });

// Shows the 2 most recent pending items overall, regardless of type (could
// be 2 statements, 2 SAIs, or one of each).
//
// This fetches on its own instead of going through the shared
// state.statements slice/getStatements thunk: that slice is also used by
// CaseStatementsPanel (for both the Statements and SAI pages, via
// isInterview), which only refetches when caseId changes and clears its
// cache on unmount. Sharing it here caused a real bug - this component would
// prime the cache with an unfiltered fetch, and whichever page you visited
// first would reuse that stale, wrongly-filtered (or unfiltered) data
// instead of fetching its own. Keeping our own local fetch avoids touching
// that cache at all.
const RecentPendingStatements = ({ caseId }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { trackException } = useAppInsights();
  const [data, setData] = useState(null);

  useEffect(() => {
    dispatch(
      parseFetchOptions(async options => {
        const response = await fetch(getStatementsUrl(caseId), {
          method: "GET",
          headers: options.headers
        }).catch(() => {
          trackException(`Failed to get statements from case: ${caseId}`);
        });

        if (response && response.ok) {
          setData(await response.json());
        } else {
          trackException(`Failed to get statements from case: ${caseId}`);
        }
      })
    );
  }, [caseId, dispatch, trackException]);

  if (!data) return null;

  const pending = data.filter(item => PENDING_STATES.includes(item.state));
  const items = sortByMostRecent(pending).slice(0, 2);

  if (items.length === 0) return null;

  const handleClick = item => () => {
    const isSai =
      item.requestSource === statementRequestSources.SELF_ADMINISTERED;
    history.push(
      getRoute(isSai ? urls.saiForm : urls.statement, {
        caseId,
        itemId: item.id
      })
    );
  };

  return (
    <RecipientsProvider>
      <div className={classes.root}>
        {items.map(item => (
          <PendingStatementItem
            key={item.id}
            statement={item}
            onClick={handleClick(item)}
          />
        ))}
      </div>
    </RecipientsProvider>
  );
};

RecentPendingStatements.propTypes = {
  caseId: PropTypes.string.isRequired
};

export default RecentPendingStatements;
